import { Component, OnInit, Optional } from '@angular/core';
import { SideNavService } from '../../layouts/side-menu/sidenav/sidenav.service';
import { ActivatedRoute } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { AccountService } from '../../account/account.service';
import { AngularFireDatabase } from '@angular/fire/database';
import { formatDate } from '@angular/common';
import { environment } from 'src/environments/environment';
import { returnBrowserTimeZone } from 'src/app/utils';

@Component({
  selector: 'app-device-troubleshot',
  templateUrl: './device-troubleshot.component.html',
  styleUrls: ['./device-troubleshot.component.css']
})
export class DeviceTroubleshotComponent implements OnInit {
  constructor(
    private sideNavService: SideNavService,
    private accountService: AccountService,
    private router: ActivatedRoute,
    private spinner: NgxSpinnerService,
    @Optional() private firebase: AngularFireDatabase
  ) {}

  deviceId: any;
  error: any;
  deviceArray: any = [];
  rawData: any;
  rawActuatorData: any;
  connectedSensor: any[] = [];
  connectedActuator: any[] = [];
  isRawDataError = false;
  validity = false;
  inputRaw: any;
  inputValidation = false;
  expectedFormat: any;
  sensorInDevice: any;
  actuatorInDevice: any;
  rawDataTemplate: any;
  newSensorData: any;
  currentTime: string = '-';
  sensorValue: any;
  lastRawDataValue: any;
  showTime: boolean = false;
  private lastTimeMs: number = 0;

  ngOnInit() {
    this.router.params.subscribe(params => {
      this.deviceId = params['deviceId'];
    });
    this.spinner.show();
    this.getDeviceData();
    if (environment.useFirebase) {
      this.getDataFromFirebase();
    }
  }

  isSideNavVisible() {
    return this.sideNavService.isSideNavVisible;
  }

  private normalizeRawString(raw: string | null | undefined): string {
    if (!raw) return '';
    const s = String(raw);
    const pipe = s.indexOf('|');
    return pipe === -1 ? s : s.slice(pipe + 1);
  }

  private toMillis(input: any): number | null {
    if (input == null) return null;
    if (typeof input === 'object' && input.hasOwnProperty('$date')) {
      const v = (input as any)['$date'];
      const ms = new Date(v).getTime();
      return isNaN(ms) ? null : ms;
    }
    if (typeof input === 'object' && input.hasOwnProperty('millis')) {
      const v: any = (input as any)['millis'];
      const n = typeof v === 'string' ? Number(v) : v;
      return typeof n === 'number' && isFinite(n) ? n : null;
    }
    if (typeof input === 'number') {
      if (input < 1e12) return Math.round(input * 1000);
      return input;
    }
    if (typeof input === 'string') {
      const ms = new Date(input).getTime();
      return isNaN(ms) ? null : ms;
    }
    return null;
  }

  private updateCurrentTimeIfNew(ms: number | null) {
    if (ms == null) return;
    if (!isFinite(ms)) return;
    if (ms <= this.lastTimeMs) return;
    const formatted = returnBrowserTimeZone(ms);
    if (!formatted) return;
    this.currentTime = formatted;
    this.showTime = true;
    this.lastTimeMs = ms;
  }

  setCurrentTimeFromDevice(content: any): void {
    if (!content) return;
    const lastSeenMs = this.toMillis(content.lastSeen);
    if (lastSeenMs != null) {
      this.updateCurrentTimeIfNew(lastSeenMs);
      return;
    }
    const map = content.sensorMap || null;
    if (map && typeof map === 'object') {
      let maxMs: number | null = null;
      const keys = Object.keys(map);
      for (let i = 0; i < keys.length; i++) {
        const s = (map as any)[keys[i]];
        const raw = s && s.time ? (s.time.$date ? s.time.$date : s.time) : null;
        const t = this.toMillis(raw);
        if (t != null) maxMs = maxMs == null || t > maxMs ? t : maxMs;
      }
      this.updateCurrentTimeIfNew(maxMs);
    }
  }

  getDeviceData() {
    this.accountService.getDevice(this.deviceId).subscribe(
      res => {
        this.deviceArray = res.content;
        this.rawData = res.content.lastDecodeData || '';
        this.rawActuatorData = res.content.lastRawActuatorData || '';
        this.isRawDataError = !res.content.lastDecodeData && !res.content.lastRawActuatorData;
        this.connectedSensor = res.content.sensorCodes;
        this.sensorInDevice = res.content.sensors;
        this.actuatorInDevice = res.content.actuators;
        this.connectedActuator = res.content.actuatorCodes;
        this.setCurrentTimeFromDevice(res.content);
        this.createExpectedMessageFormat(res.content.sensorCodes, res.content.actuatorCodes);
        this.deviceRowData(res.content.lastDecodeData);
        let splitExpactedFormat
        if (this.rawData && this.rawActuatorData) {
          const splitExpactedFormat = this.expectedFormat.split('*');
          const sensorDataValid = this.lastRawDataValidation(
            this.deviceRowData(this.normalizeRawString(res.content.lastDecodeData)),
            this.deviceRowData(splitExpactedFormat[0])
          );
          const actuatorDataValid = this.lastRawDataValidation(
            this.deviceRowData(this.normalizeRawString(res.content.lastRawActuatorData)),
            this.deviceRowData(splitExpactedFormat[1])
          );
          // TRUE if any one valid
          this.validity = sensorDataValid || actuatorDataValid;

        } else if (this.rawData) {
          const sensorDataValid = this.lastRawDataValidation(
            this.deviceRowData(this.normalizeRawString(res.content.lastDecodeData)),
            this.deviceRowData(this.expectedFormat)
          );
          this.validity = sensorDataValid;

        } else if (this.rawActuatorData) {
          const actuatorDataValid = this.lastRawDataValidation(
            this.deviceRowData(this.normalizeRawString(res.content.lastRawActuatorData)),
            this.deviceRowData(this.expectedFormat)
          );
          this.validity = actuatorDataValid;
        } else {
          this.validity = false;
        }
        this.spinner.hide();
      },
      e => {
        if (e && e.error && e.error.errorCode === 'E1011') {
          this.error = 'Device Not Found';
        }
        this.spinner.hide();
      }
    );
  }

  getFormattedRawData(): string {
    if (this.isRawDataError) return '';
    if(this.rawData){
      return this.rawData + (this.rawActuatorData ? '*' + this.rawActuatorData : '');
    }else{
      return this.rawActuatorData ;
    }
  }

  deviceRowData(value: any) {
    if (!value) return [];
    var normalized = this.normalizeRawString(typeof value === 'string' ? value : String(value));
    normalized = normalized.replace('*', ';');
    const parts = normalized.split(';').filter(function (v) { return v; });
    const keys = parts
      .map(function (item) { return item.split(':')[0]; })
      .map(function (k) { return k.indexOf('|') !== -1 ? k.split('|').pop() as string : k; });
    this.sensorValue = parts.map(function (i) { return i.split(':'); }).map(function (i) { return parseFloat(i[1]); });
    return keys;
  }

  checkInputDataValidity() {
    const inputEl = document.getElementById('inputRawData') as HTMLInputElement;
    const actuatorEl = document.getElementById('inputRawActuatorData') as HTMLInputElement;
    const inputValue = inputEl && typeof inputEl.value === 'string' ? inputEl.value : '';
    const actuatorValue = actuatorEl && typeof actuatorEl.value === 'string' ? actuatorEl.value : '';
    const inputFormat = this.deviceRowData(this.normalizeRawString(inputValue));
    const actuatorFormat = this.deviceRowData(this.normalizeRawString(actuatorValue));
    const validation = !(this.sensorValue && this.sensorValue.some(function (n: any) { return Number.isNaN(n); }));
    const isSensorValid = this.lastRawDataValidation(inputFormat, this.deviceRowData(this.expectedFormat));
    const isActuatorValid = this.lastRawDataValidation(actuatorFormat, this.deviceRowData(this.expectedFormat));
    this.inputValidation = (isSensorValid || isActuatorValid) && !!validation;
  }

  lastRawDataValidation(array1: any[], array2: any[]) {
    if (!Array.isArray(array1) || !Array.isArray(array2)) return false;
    let areArraysEqual = false;
    const sortedArray1 = array1.slice().sort();
    const sortedArray2 = array2.slice().sort();
    if (sortedArray1.indexOf('DT') !== -1 || sortedArray1.indexOf('ZZ') !== -1) {
      areArraysEqual = sortedArray1.some(function (element, index) { return element === sortedArray2[index]; });
    } else {
      if (sortedArray1.length === sortedArray2.length) {
        areArraysEqual = sortedArray1.every(function (element, index) { return element === sortedArray2[index]; });
      }
    }
    return areArraysEqual;
  }

  splitArray(data: string) {
    const arraySplit = data.split(';');
    const innerSplit = arraySplit.map(function (item) { return item.split(/[-:]/); });
    return innerSplit.map(function (item) { return item[1]; }).slice().sort();
  }

  createExpectedMessageFormat(sensors: string[], actuators: string[]) {
    if (sensors.length === 0 && actuators.length === 0) {
      this.expectedFormat = 'There is no sensor or actuators connected.';
      return;
    }
    const self = this;
    const parseRawData = function (raw: string) {
      const dataMap: Record<string, number> = {};
      const n = self.normalizeRawString(raw);
      if (!n) return dataMap;
      const items = n.replace('*', '').split(';').filter(function (v) { return v; });
      items.forEach(function (item) {
        const parts = item.split(':');
        if (parts.length === 2) {
          const codeParts = parts[0].split('-');
          const code = codeParts.length > 1 ? codeParts[1] : undefined;
          const value = parseFloat(parts[1]) || 0;
          if (code) dataMap[code] = value;
        }
      });
      return dataMap;
    };
    const sensorValue = Object.assign({}, parseRawData(this.rawData), parseRawData(this.rawActuatorData));
    const sensorFormat = this.connectedSensor
      .map(function (device, index) {
        const value = sensorValue.hasOwnProperty(device) ? (sensorValue as any)[device] : 0;
        return index + '-' + device + ':' + value;
      })
      .join(';');
    const actuatorFormat = this.connectedActuator
      .map(function (device, index) {
        const value = sensorValue.hasOwnProperty(device) ? (sensorValue as any)[device] : 0;
        return index + '-' + device + ':' + value;
      })
      .join(';');
    this.expectedFormat = sensorFormat && actuatorFormat ? sensorFormat + '*' + actuatorFormat : sensorFormat + actuatorFormat;
  }

  getDataFromFirebase() {
    const data = this.firebase.list('devices/' + this.deviceId).valueChanges();
    const deviceDetails = this.firebase.object('deviceDetails/' + this.deviceId).valueChanges();

    deviceDetails.subscribe((details: any) => {
      if (!details) return;
      this.rawData = details.lastDecodeData ? details.lastDecodeData : (this.rawData ? this.rawData : '');
      this.rawActuatorData = details.lastRawActuatorData ? details.lastRawActuatorData : (this.rawActuatorData ? this.rawActuatorData : '');
      this.deviceRowData(this.rawData);
      this.isRawDataError = !this.rawData && !this.rawActuatorData;
      this.setCurrentTimeFromDevice(details);
      this.validity =
        this.lastRawDataValidation(
          this.deviceRowData(this.normalizeRawString(this.rawData)),
          this.deviceRowData(this.expectedFormat)
        ) ||
        this.lastRawDataValidation(
          this.deviceRowData(this.normalizeRawString(this.rawActuatorData)),
          this.deviceRowData(this.expectedFormat)
        ) ||
        this.lastRawDataValidation(
          this.deviceRowData(
            this.normalizeRawString((this.rawData ? this.rawData : '') + (this.rawActuatorData ? '*' + this.rawActuatorData : ''))
          ),
          this.deviceRowData(this.expectedFormat)
        );
    });

    data.subscribe((items: any[]) => {
      this.newSensorData = items;
      let maxMs: number | null = null;
      for (let i = 0; i < this.newSensorData.length; i++) {
        const sensor: any = this.newSensorData[i];
        let ms: number | null = null;
        if (sensor && sensor.time && sensor.time.millis != null) {
          ms = this.toMillis(sensor.time.millis);
        } else if (sensor && sensor.time && sensor.time.$date != null) {
          ms = this.toMillis(sensor.time.$date);
        } else if (sensor && sensor.time != null) {
          ms = this.toMillis(sensor.time);
        }
        if (ms != null) {
          maxMs = maxMs == null || ms > maxMs ? ms : maxMs;
          const dateObject = new Date(ms);
          const timeObject = dateObject.toTimeString().split(/\s/)[0];
          const dateStr = formatDate(dateObject, 'yyyy-MM-dd', 'lk');
          sensor.time = dateStr + ' ' + timeObject;
        }
      }
      this.updateCurrentTimeIfNew(maxMs);
    });

    this.spinner.hide();
  }
}
